import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { clearDevicePushSubscription } from '../utils/devicePush';
import { detectPlatform } from '../utils/installPwa';
import { installAuthInterceptor } from '../utils/authInterceptor';
import { queryClient, QUERY_CACHE_KEY } from '../lib/queryClient';
import {
  saveSession,
  clearSession,
  currentPublicRefresh,
} from '../utils/sessionStorage';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = authenticated
  const [loading, setLoading] = useState(true);

  // Feb 2026 — Clear the react-query cache + persisted localStorage blob
  // whenever the user transitions from authed → un-authed (explicit logout
  // OR session expiry). Prevents the next user on the same device from
  // painting stale data from the previous user's cache. Isolated as a
  // helper so all three logout paths (logout(), checkAuth fail,
  // interceptor 401 fail) can call it consistently.
  //
  // Phase 2 (Feb 2026) — Also wipe the dual-transport session envelope
  // (`am_session_v1`) so a logged-out client can't silently re-authenticate
  // from a stale bearer, and a subsequent login on the same device
  // doesn't leak the previous user's tokens to the next tab.
  const clearAuthCache = useCallback(() => {
    try {
      queryClient.clear();
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(QUERY_CACHE_KEY);
      }
      clearSession();
    } catch (_) {
      // Non-fatal — cache clear is best-effort.
    }
  }, []);

  // Phase 6 Tier 1 (Feb 2026) — Cold-mount session restoration.
  // Previously: `/api/auth/me` 401 → setUser(false) → forced login even when
  // the 90-day refresh token was still valid. Now we try /auth/refresh once
  // before declaring the user logged out. Critical for SA / dispatchers who
  // open the PWA hours/days after their access token expired.
  //
  // Phase 2 (Feb 2026) — When cookies have been evicted (iOS PWA under
  // storage pressure), we forward the localStorage `refresh_public` in the
  // POST body so the server can rehydrate the session without them.
  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        try {
          const publicRefresh = currentPublicRefresh();
          const refreshBody = publicRefresh ? { refresh_public: publicRefresh } : {};
          const refreshResp = await axios.post(
            `${API_URL}/api/auth/refresh`,
            refreshBody,
            { withCredentials: true },
          );
          // Phase 2 — persist the rotated dual-transport pair so the
          // next visibilitychange / cold-start finds a fresh envelope.
          saveSession(refreshResp?.data || {});
          // Refresh succeeded — re-fetch user identity with the new access cookie
          const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
          setUser(data);
        } catch (_refreshErr) {
          // Feb 2026 — refresh token dead too. Wipe any persisted cache
          // from the previous session so the login screen (and next user)
          // can't accidentally paint stale data.
          clearAuthCache();
          setUser(false);
        }
      } else {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, [clearAuthCache]);

  // Phase 6 Tier 1 — Install global axios 401 interceptor at provider boot so
  // EVERY authed call gets a silent refresh-and-retry on a single 401. Without
  // this, dispatcher actions that hit a brief 401 window during refresh would
  // visibly fail (e.g., order assignment buttons going dead until manual reload).
  useEffect(() => {
    const uninstall = installAuthInterceptor({
      onUnauthorized: () => {
        clearAuthCache();
        setUser(false);
      },
    });
    return uninstall;
  }, [clearAuthCache]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Phase 7-lite — session persistence across iOS app switching (Maps → A&M etc).
  // When the PWA backgrounds for >30s iOS pauses the JS runtime. On return the
  // access token may have expired (1h lifetime) even though the refresh token
  // is still valid (90d). Silently re-validate session on visibilitychange so
  // drivers/dispatchers never see a stale 401 → forced re-login loop.
  //
  // Feb 2026 (Phase A) — collapsed the previous 3-request chain
  // (`/auth/me` → `/auth/refresh` → `/auth/me`) into a single call to the new
  // `/auth/session` endpoint. Fewer requests = fewer 401 race windows = no
  // more mid-delivery logouts. The endpoint refreshes AND returns the user in
  // one round-trip. Only flips to logged-out on a hard refresh-token failure.
  useEffect(() => {
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      // Only re-validate if we believe we ARE logged in. Avoids spamming the
      // session heartbeat on the login screen.
      if (user && user.id) {
        try {
          // Phase 2 (Feb 2026) — Attach the localStorage refresh_public in
          // the body so the heartbeat still works after iOS has evicted
          // both auth cookies while the app was backgrounded.
          const publicRefresh = currentPublicRefresh();
          const heartbeatBody = publicRefresh ? { refresh_public: publicRefresh } : {};
          const { data } = await axios.post(
            `${API_URL}/api/auth/session`,
            heartbeatBody,
            { withCredentials: true },
          );
          // Phase 2 — persist rotated tokens (server issues fresh ones on
          // every heartbeat). Same object shape as login response.
          saveSession(data || {});
          // Keep the local user snapshot fresh — the heartbeat returns the
          // same shape as /auth/me so ancillary fields (referral_code, tier,
          // active status) refresh alongside the session.
          if (data && data.id) setUser(data);
        } catch (err) {
          if (err.response?.status === 401) {
            // Refresh token expired too — surface as logged-out.
            clearAuthCache();
            setUser(false);
          }
          // Any other error (network hiccup, 5xx) → leave the user state
          // alone. They'll retry naturally on the next visibilitychange.
        }
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [user, clearAuthCache]);

  const login = async (email, password) => {
    // Phase 7-lite — DO NOT clear push subscription on login. The backend's
    // upsert_subscription() is keyed by endpoint and overwrites the user_id
    // automatically (shared-phone case is handled there). Wiping here was the
    // root cause of "I got logged out and never see pushes again" — a session
    // expiry → silent re-login cycle was deleting subs without re-subscribing
    // reliably. Now subs survive login/refresh cycles.
    //
    // Phase 7-lite — capture standalone (PWA vs browser) + device class so we
    // can answer "are people actually using the PWA?" in the admin dashboard.
    const p = detectPlatform();
    const ua_class = p.isIPad ? 'ipad'
      : p.isIPhone ? 'ios'
      : p.isAndroid ? 'android'
      : 'desktop';
    const { data } = await axios.post(
      `${API_URL}/api/auth/login`,
      {
        email,
        password,
        is_standalone: p.isStandalone,
        ua_class,
        ios_major: p.iosMajor || null,
      },
      { withCredentials: true },
    );
    // Feb 2026 — Clear any residual cache from a previous account BEFORE
    // switching identity. Belt-and-braces alongside the logout-side clear.
    clearAuthCache();
    // Phase 2 (Feb 2026) — persist the dual-transport pair AFTER
    // clearAuthCache() (which wipes any prior envelope) so the new
    // user's tokens land cleanly in localStorage.
    saveSession(data || {});
    setUser(data);
    return data;
  };

  const register = async (userData) => {
    const { data } = await axios.post(`${API_URL}/api/auth/register`, userData, { withCredentials: true });
    clearAuthCache();
    // Phase 2 (Feb 2026) — persist dual-transport pair from register.
    saveSession(data || {});
    setUser(data);
    return data;
  };

  const logout = async () => {
    // Phase 5.9.6 — unsubscribe device from push BEFORE the session cookie
    // is cleared. Without this, the server keeps pushing notifications to a
    // device whose user has logged out.
    await clearDevicePushSubscription();
    await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    clearAuthCache();
    setUser(false);
  };

  const updateProfile = async (profileData) => {
    const { data } = await axios.put(`${API_URL}/api/users/profile`, profileData, { withCredentials: true });
    setUser(prev => ({ ...prev, ...data }));
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
